import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Plug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { AccountingConnectButtons } from "@/components/ConnectAccounting";

export const XeroConnect = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoSend, setAutoSend] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const autoStarted = useRef(false);

  const load = () => api.get("/integrations/xero/status").then(({ data }) => { setStatus(data); setAutoSend(!!data.auto_send); }).catch(() => setStatus({ connected: false, configured: false }));
  useEffect(() => { load(); }, []);

  // Auto-start the Xero connect flow when arriving from signup (?connect=xero).
  useEffect(() => {
    if (autoStarted.current || !status) return;
    if (params.get("connect") === "xero" && status.configured && !status.connected) {
      autoStarted.current = true;
      const next = new URLSearchParams(params); next.delete("connect"); setParams(next, { replace: true });
      connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/xero/connect", { params: { origin: window.location.origin } });
      window.location.href = data.auth_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not start Xero connect"); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/integrations/xero/disconnect"); toast.success("Xero disconnected"); await load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleAutoSend = async (val) => {
    setAutoSend(val);
    setAutoBusy(true);
    try {
      await api.put("/integrations/xero/settings", { auto_send: val });
      toast.success(val ? "Auto-send to Xero on — approved applications create a draft invoice" : "Auto-send to Xero off");
    } catch (err) {
      setAutoSend(!val);
      toast.error(apiError(err.response?.data?.detail) || "Could not update setting");
    } finally { setAutoBusy(false); }
  };

  if (!status) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="xero-settings-section">
      <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Plug className="h-5 w-5 text-[#13B5EA]" /> Accounting</h2>
      <p className="text-sm text-slate-500 mt-0.5 mb-4">Connect your accounting software to push a valuation straight across as a draft sales invoice — CIS is calculated and your reverse-charge VAT applied. You approve it before it hits your books. Xero is live now; QuickBooks and Sage are coming soon.</p>

      {status.connected ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="xero-connected">
            <div className="flex items-center gap-2 text-sm text-emerald-800">
              <CheckCircle2 className="h-5 w-5" />
              <span>Connected to <strong>{status.organisation}</strong> in Xero{status.connected_by ? ` · by ${status.connected_by}` : ""}</span>
            </div>
            <Button size="sm" variant="outline" onClick={disconnect} disabled={busy} data-testid="xero-disconnect-btn" className="border-slate-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disconnect"}
            </Button>
          </div>
          <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-slate-800">Auto-send to Xero on approval</p>
              <p className="text-xs text-slate-500 mt-0.5">When a client approves an Application for Payment, TradeOff automatically creates (or updates) the draft valuation invoice in Xero. You still review &amp; approve it in Xero before it hits your books.</p>
            </div>
            <Switch checked={autoSend} disabled={autoBusy} onCheckedChange={toggleAutoSend} data-testid="xero-auto-send-toggle" className="mt-0.5 shrink-0" />
          </div>
          <ComingSoonRow />
        </div>
      ) : !status.configured ? (
        <div className="space-y-2.5">
          <p className="text-sm text-amber-600 mb-1">Xero isn't configured on this environment yet.</p>
          <ComingSoonRow />
        </div>
      ) : (
        <AccountingConnectButtons onConnectXero={connect} busy={busy} />
      )}
    </div>
  );
};

const ComingSoonRow = () => (
  <p className="text-xs text-slate-400 flex items-center gap-2 pt-1">
    <span className="h-2 w-2 rounded-full bg-[#2CA01C]" /> QuickBooks
    <span className="h-2 w-2 rounded-full bg-[#00D639] ml-2" /> Sage
    <span className="ml-1">— direct sync coming soon (CSV export available now).</span>
  </p>
);

export default XeroConnect;
